import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "sectionLink", "contentSection" ]

  connect() {
    // Detect summary mode via data attribute
    this.isSummaryMode = this.element.dataset.summary === 'true'

    // Obtener el step_index de la URL si existe
    const urlParams = new URLSearchParams(window.location.search)
    const stepIndex = urlParams.get('step_index')

    // Set default index based on mode
    if (this.isSummaryMode) {
      this.index = stepIndex ? parseInt(stepIndex) : 7  // Default to 7 (Resumen) for summary mode
    } else {
      this.index = stepIndex ? parseInt(stepIndex) : 0  // Default to 0 for consultation form
    }

    // Show the correct section immediately
    this.showSection(this.index)

    if (!this.isSummaryMode) {
      this.addNavigationFooters()
    }

    // Setup mobile accordion behavior
    this.setupMobileAccordion()
  }

  setupMobileAccordion() {
    const cardBody = this.element.querySelector('.col-md-3 .card-body')
    const listGroup = this.element.querySelector('.col-md-3 .list-group')

    if (!cardBody || !listGroup) return

    // Function to update display based on window size
    const updateDisplay = () => {
      if (window.innerWidth <= 768) {
        // Mobile: collapse by default
        if (!cardBody.classList.contains('collapsed')) {
          listGroup.style.display = 'none'
          cardBody.classList.add('collapsed')
        }
      } else {
        // Desktop: always show
        listGroup.style.display = 'block'
        cardBody.classList.remove('collapsed')
      }
    }

    // Initially set display
    updateDisplay()

    // Toggle on click (only works on mobile due to check inside)
    cardBody.addEventListener('click', (e) => {
      if (window.innerWidth <= 768) {
        if (listGroup.style.display === 'none') {
          listGroup.style.display = 'block'
          cardBody.classList.remove('collapsed')
        } else {
          listGroup.style.display = 'none'
          cardBody.classList.add('collapsed')
        }
      }
    })

    // Listen for window resize
    window.addEventListener('resize', updateDisplay)
  }

  // Navigate to a section when a sidebar link is clicked
  navigateTo(event) {
    event.preventDefault()
    this.index = parseInt(event.currentTarget.dataset.index)
    this.showSection(this.index)
    this.updateURL()

    // Close accordion on mobile after selection
    if (window.innerWidth <= 768) {
      const listGroup = this.element.querySelector('.col-md-3 .list-group')
      const cardBody = this.element.querySelector('.col-md-3 .card-body')
      if (listGroup && cardBody) {
        listGroup.style.display = 'none'
        cardBody.classList.add('collapsed')
      }
    }
  }

  // Check if appointment is already completed
  isAppointmentCompleted() {
    const consultationForm = this.contentSectionTargets[6].querySelector('form');
    return consultationForm && consultationForm.querySelector('input[name="appointment[status]"][value="completed"]');
  }

  // Go to the next section
  next() {
    if (this.isSummaryMode) return
    // Show alert for step 6 (información de consulta) only if not already completed
    if (this.index === 6 && !this.isAppointmentCompleted()) {
      if (!confirm('Al guardar los cambios la consulta se marcará como completada. ¿Desea continuar?')) {
        return
      }
    }
    
    // Auto-save current form before navigating
    this.autoSaveCurrentForm(() => {
      if (this.index < this.contentSectionTargets.length - 1) {
        this.index++
        this.showSection(this.index)
        this.updateURL()
      }
    })
  }

  // Auto-save current form before navigation
  autoSaveCurrentForm(callback) {
    if (this.isSummaryMode) {
      if (callback) callback()
      return
    }
    const currentSection = this.contentSectionTargets[this.index]
    const form = currentSection.querySelector('form')
    
    // Only auto-save for steps that have forms to save
    if (form && this.shouldAutoSaveStep(this.index)) {
      // Add hidden field to indicate next step after save
      const nextStepField = document.createElement('input')
      nextStepField.type = 'hidden'
      nextStepField.name = 'next_step'
      nextStepField.value = this.index + 1
      form.appendChild(nextStepField)
      
      // Submit form
      form.requestSubmit()
    } else {
      // No form to save, just navigate
      if (callback) callback()
    }
  }

  // Check if a step should auto-save
  shouldAutoSaveStep(stepIndex) {
    if (this.isSummaryMode) return false
    return [0, 1, 2, 3, 5, 6].includes(stepIndex)
  }

  // Go to the previous section
  previous() {
    if (this.isSummaryMode) return
    if (this.index > 0) {
      this.index--
      this.showSection(this.index)
      this.updateURL()
    }
  }

  // Save the current form
  save(event) {
    const sectionIndex = parseInt(event.currentTarget.dataset.index)
    event.preventDefault()

    if (this.isSummaryMode) {     
      const form = this.contentSectionTargets[sectionIndex].querySelector('form')
      if (form) {
        // Add hidden field with current step_index
        const stepIndexField = document.createElement('input')
        stepIndexField.type = 'hidden'
        stepIndexField.name = 'step_index'
        stepIndexField.value = sectionIndex
        form.appendChild(stepIndexField)
        
        form.requestSubmit()
      }
      return
    } else {
      if (sectionIndex === 0) {
        const patientForm = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (patientForm) {
          patientForm.requestSubmit()
        }
      } else if (sectionIndex === 1) {
        const nutritionalProfileForm = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (nutritionalProfileForm) {
          nutritionalProfileForm.requestSubmit()
        }
      } else if (sectionIndex === 2) {
        const foodHistoryForm = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (foodHistoryForm) {
          foodHistoryForm.requestSubmit()
        }
      } else if (sectionIndex === 3) {
        // Check if there are diagnostic fields before allowing save
        const diagnosticFields = this.contentSectionTargets[sectionIndex].querySelectorAll('.diagnostic-field')
        if (diagnosticFields.length === 0) {
          alert('No hay campos de diagnóstico configurados. Contacte al administrador.')
          return
        }
        
        const diagnosticForm = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (diagnosticForm) {
          diagnosticForm.requestSubmit()
        }
      } else if (sectionIndex === 5) {
        // Check if there are genetic history fields before allowing save
        const geneticFields = this.contentSectionTargets[sectionIndex].querySelectorAll('input[name^="genetic_histories"], select[name^="genetic_histories"]')
        if (geneticFields.length === 0) {
          alert('No hay campos de antecedentes genéticos configurados. Contacte al administrador.')
          return
        }
        
        const geneticForm = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (geneticForm) {
          geneticForm.requestSubmit()
        }
      } else if (sectionIndex === 6) {
        // Show alert for step 6 (información de consulta) only if not already completed
        if (!this.isAppointmentCompleted()) {
          if (!confirm('Al guardar los cambios la consulta se marcará como completada. ¿Desea continuar?')) {
            return
          }
        }
        
        const consultationInfoForm = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (consultationInfoForm) {
          consultationInfoForm.requestSubmit()
        }
      } else {
        // Logic for other save buttons
        const form = this.contentSectionTargets[sectionIndex].querySelector('form')
        if (form) {
          form.requestSubmit()
        }
      }
    }
  }
  
  // Core function to display the correct section
  showSection(index) {
    // Hide all sections first
    this.contentSectionTargets.forEach((section) => {
      section.style.display = 'none'
    })

    // Remove active class from all links
    this.sectionLinkTargets.forEach((link) => {
      link.classList.remove('active')
    })

    // Show the correct section based on data-index
    this.contentSectionTargets.forEach((section, i) => {
      const sectionIndex = parseInt(this.sectionLinkTargets[i].dataset.index)
      if (sectionIndex === index) {
        section.style.display = 'block'
        this.sectionLinkTargets[i].classList.add('active')
      }
    })

    // Clear hash when showing a different section (not food preferences)
    if (index !== 4) { // 4 is the food preferences step
      if (window.location.hash) {
        window.history.replaceState(null, null, window.location.pathname + window.location.search)
      }
    }
  }

  // Dynamically create and append footers to each section
  addNavigationFooters() {
    this.contentSectionTargets.forEach((section, index) => {
      const footer = this.createSectionFooter(index)
      section.appendChild(footer)
    })
  }
  
  createSectionFooter(index) {
    const footer = document.createElement('div');
    footer.className = 'd-flex justify-content-between p-3 border-top bg-white shadow-sm position-fixed bottom-0 start-0 w-100 consultation-footer';
    footer.style.zIndex = '1000';

    // Previous Button
    const prevButton = document.createElement('button');
    prevButton.className = 'btn btn-secondary';
    prevButton.innerHTML = '<i class="fas fa-arrow-left mr-2"></i> Anterior';
    prevButton.dataset.action = 'click->consultation-navigation#previous';
    if (index === 0) {
      prevButton.style.visibility = 'hidden'; // Hide but keep space
    }
    
    const rightGroup = document.createElement('div');

    if (index === 8) {
      // SUMMARY STEP: No buttons at all
      // Don't add any buttons to rightGroup
      
    } else if (index === this.contentSectionTargets.length - 1) {
      // LAST STEP ("Resumen"): Show only the "Finalize" button
      const finalizeButton = document.createElement('button');
      finalizeButton.className = 'btn btn-success';
      finalizeButton.innerHTML = '<i class="fas fa-flag-checkered mr-2"></i> Finalizar y Marcar como Completada';
      // TODO: Add action for finalizeButton, e.g., 'click->consultation-navigation#finalize'
      rightGroup.appendChild(finalizeButton);

    } else {
      // INTERMEDIATE STEPS: Show "Save" and "Next"
      const saveButton = document.createElement('button');
      saveButton.className = 'btn btn-success';
      saveButton.innerHTML = '<i class="fas fa-save mr-2"></i>';
      saveButton.dataset.action = 'click->consultation-navigation#save';
      saveButton.dataset.index = index;
      
      // Disable save button for certain steps
      if (index !== 0 && index !== 1 && index !== 2 && index !== 3 && index !== 5 && index !== 6) {
        saveButton.disabled = true; // Disabled for other forms for now
      }
      
      // For step 3 (diagnóstico), check if there are diagnostic fields
      if (index === 3) {
        const diagnosticFields = this.contentSectionTargets[index].querySelectorAll('.diagnostic-field');
        if (diagnosticFields.length === 0) {
          saveButton.disabled = true;
          saveButton.title = 'No hay campos de diagnóstico configurados';
        }
      }
      
      // For step 5 (antecedentes genéticos), check if there are genetic history fields
      if (index === 5) {
        const geneticFields = this.contentSectionTargets[index].querySelectorAll('input[name^="genetic_histories"], select[name^="genetic_histories"]');
        if (geneticFields.length === 0) {
          saveButton.disabled = true;
          saveButton.title = 'No hay campos de antecedentes genéticos configurados';
        }
      }
      
      // Hide save button for step 4 (preferencias alimentarias), step 7 (dieta sugerida), and step 8 (resumen)
      if (index === 4 || index === 7 || index === 8) {
        saveButton.style.display = 'none';
      }
      
      // Change save button text for step 6 (información de consulta)
      if (index === 6) {
        // Check if consultation is already completed
        const consultationForm = this.contentSectionTargets[index].querySelector('form');
        const isCompleted = consultationForm && consultationForm.querySelector('input[name="appointment[status]"][value="completed"]');
        
        if (isCompleted) {
          saveButton.innerHTML = '<i class="fas fa-save mr-2"></i>';
          saveButton.className = 'btn btn-success';
        } else {
          saveButton.innerHTML = '<i class="fas fa-save mr-2"></i>';
          saveButton.className = 'btn btn-success'; // Cambiar a verde para indicar finalización
        }
      }
      
      const nextButton = document.createElement('button');
      nextButton.className = 'btn btn-primary';
      nextButton.innerHTML = 'Siguiente <i class="fas fa-arrow-right ml-2"></i>';
      nextButton.dataset.action = 'click->consultation-navigation#next';

      rightGroup.appendChild(saveButton);
      rightGroup.appendChild(nextButton);
    }
    
    const leftGroup = document.createElement('div');
    leftGroup.appendChild(prevButton);

    footer.appendChild(leftGroup);
    footer.appendChild(rightGroup);

    return footer;
  }

  // Update URL with current step index
  updateURL() {
    const url = new URL(window.location)
    url.searchParams.set('step_index', this.index)
    
    // Clear any hash fragments when navigating between sections
    url.hash = ''
    
    window.history.replaceState({}, '', url)
  }
} 